import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { theme } from '@arnold/common';
import { useTranslation } from 'react-i18next';
import type { NewReport } from '../../hooks/useNewReport';
import { ColumnType } from '../../components/AnswerGroupTable';
import { TableTestPage } from '../../pages/TableTestPage';
import { Action, Field } from '../../pages/tablePlaygroundStyles';
import { newDefinitionId, type ReportComponent, type ReportTableConfiguration } from '../types';
import { useTableReport } from '../tableComparisons';
import { parseReportDefinition, validateDefinitionQuestions } from '../validation';
import { useDefinition } from '../ReportDefinitionContext';
import { EditorModal } from './EditorModal';

type TableComponent = Extract<ReportComponent, { type: 'table' }>;

export function TableEditorModal({
  report,
  component,
  language,
  onApply,
  onClose,
}: {
  report: NewReport;
  component?: TableComponent;
  language: string;
  onApply: (component: TableComponent) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('newReport');
  const { document: definition, metricErrors } = useDefinition();
  const [initial] = useState<TableComponent>(
    () =>
      component ?? {
        id: newDefinitionId(),
        type: 'table',
        label: { [language]: t('definition.newTable') },
        table: {
          columns: [{ id: newDefinitionId(), type: ColumnType.LABEL, label: '' }],
          rowMode: 'teamsAndParticipants',
          pageSize: 10,
          sortingEnabled: true,
          searchEnabled: true,
          columnOrderingEnabled: false,
          columnResizeEnabled: false,
          horizontalScroll: false,
        },
      },
  );
  const [label, setLabel] = useState(initial.label);
  const [configuration, setConfiguration] = useState<ReportTableConfiguration>(initial.table);
  const tableReport = useTableReport(report, initial.table.comparisonGroups, language);
  const edited = useMemo(
    (): TableComponent => ({ ...initial, label, table: configuration }),
    [initial, label, configuration],
  );
  const error = useMemo(() => {
    try {
      const document = parseReportDefinition({
        ...definition,
        metrics: definition.metrics.filter((metric) => !metricErrors[metric.id]),
        pages: { overview: { components: [edited] } },
      });
      validateDefinitionQuestions(document, report.rawData?.questions ?? []);
      return undefined;
    } catch (error) {
      return (error as Error).message;
    }
  }, [definition, metricErrors, report.rawData?.questions, edited]);
  return (
    <EditorModal
      size="full"
      enforceFocus={false}
      title={t(component ? 'definition.configureTable' : 'definition.addTable')}
      onClose={onClose}
      footer={
        <>
          <Status>
            {error
              ? t('definition.invalid', { reason: t(`definition.validation.${error}`) })
              : t('definition.tablePreviewHelp')}
          </Status>
          <Action onClick={onClose}>{t('definition.cancel')}</Action>
          <Action primary disabled={!!error} onClick={() => onApply(edited)}>
            {t(component ? 'definition.applyTable' : 'definition.addTable')}
          </Action>
        </>
      }
    >
      <TitleField>
        {t('definition.tableTitle')} ({language})
        <input
          value={label[language] ?? ''}
          onChange={(event) => setLabel((previous) => ({ ...previous, [language]: event.target.value }))}
        />
      </TitleField>
      <TableTestPage
        report={tableReport}
        initialConfiguration={initial.table}
        onConfigurationChange={setConfiguration}
        embedded
        language={language}
      />
    </EditorModal>
  );
}
const TitleField = styled(Field)`
  max-width: 700px;
  margin-bottom: 24px;
`;
const Status = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.text.secondary};
`;
